/**
 * UseCaseDetailPage — IVT Hub Use Case detail (`/ivt/use-cases/:solutionMapId`).
 *
 * Reached from the Assets tree's clickable Structured-mode Use Case leaf. Same
 * two-column shell as the Asset Detail page (collapsible category tree on the left,
 * content on the right). Minimal first slice: show the UC name + an editable SFDC
 * tag (same write + inline-edit UX as the asset SFDC tag).
 *
 * The route key is solutionMapId (UC PK) — unambiguous for same-named UCs across
 * sales areas. useUseCaseDetail resolves it via GetUcFull into { industry, name,
 * tag }; the tag write is UpsertUcTagById({ solutionMapId, tag }), so the route key
 * is also the write key and nothing here depends on the name.
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Icon, Input, Spinner } from '@amzn/awsui-components-react';
import { getIvtClient, UpsertUcTagByIdCommand } from '../../client/IvtHubClient';
import { useAssetsData } from './hooks/useAssetsData';
import { useUseCaseDetail } from './hooks/useUseCaseDetail';
import { useStorylineTreeProps } from './hooks/useStorylineTreeProps';
import AssetNavTree from './components/AssetNavTree';
import CollapsibleTreePanel from './components/CollapsibleTreePanel';
import { saveAssetsView } from './components/AssetsViewSwitch';
import { KbFilesPanel } from './components/AssetKbFiles';
import { useKb } from './hooks/useKb';
import { useChatPanel } from '../../../context/ChatPanelContext';
import './AssetDetailPage.css';
import './UseCaseDetailPage.css';
import { denialHint } from '../../utils/authorization';

export default function UseCaseDetailPage() {
  const { solutionMapId: idParam } = useParams<{ solutionMapId: string }>();
  const solutionMapId = idParam != null && /^\d+$/.test(idParam) ? Number(idParam) : null;
  const navigate = useNavigate();

  // The left tree needs the full asset list (same ListAssets({}) as the other pages).
  const { assets, industries, reload: reloadAssets } = useAssetsData();
  const all = assets ?? [];
  const { detail, loading, error, notFound, reload } = useUseCaseDetail(solutionMapId);
  // Storyline Menu tree wiring — the tree mode is a SHARED store, so a user who picked
  // Storyline Menu elsewhere keeps it here; without this the tree would collapse to
  // all-Unassigned with no `+`. Same shared hook every tree-hosting page uses.
  const storylineTree = useStorylineTreeProps({ assets: all, industries, reloadAssets });

  return (
    <div className="ivt-assetd-page">
      <div className="ivt-assetd-shell">
        <CollapsibleTreePanel>
          {/* selectedAssetId=null: this is a UC page, no asset is "selected". UC leaf
              click → another UC detail page (same route). */}
          <AssetNavTree
            assets={all}
            selectedAssetId={null}
            onSectionClick={(view) => {
              // Industry / Topic section → Overview in that view (same as Detail page).
              saveAssetsView(view);
              navigate('/ivt/assets');
            }}
            onUseCaseClick={(id) => navigate(`/ivt/use-cases/${id}`)}
            // Structured mode: highlight the UC leaf currently open on this page.
            selectedUseCaseId={solutionMapId}
            // Storyline Menu mode: full wiring from the shared hook.
            {...storylineTree.treeProps}
          />
        </CollapsibleTreePanel>

        <div className="ivt-assetd-right">
          {loading && (
            <div className="ivt-uc-detail__loading">
              <Spinner size="large" />
              <span>加载 Use Case…</span>
            </div>
          )}
          {!loading && error && (
            <Alert type="error" header="无法加载 Use Case" action={<Button onClick={reload}>重试</Button>}>
              {error}
            </Alert>
          )}
          {!loading && !error && notFound && (
            <div className="ivt-assetd-notfound">未找到该 Use Case（id: {idParam}）。</div>
          )}
          {!loading && !error && detail && <UseCaseDetailContent detail={detail} onSaved={reload} />}
        </div>
      </div>
      {/* Create Storyline modal (opened by the Storyline Menu tree's `+`). */}
      {storylineTree.modalNode}
    </div>
  );
}

/** Right-side content: UC name (title) + editable SFDC tag. */
function UseCaseDetailContent({
  detail,
  onSaved,
}: {
  detail: { solutionMapId: number; industry: string; name: string; tag: string };
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // UC KB files — the SAME owner-generic hook + panel as the Asset detail KB tab.
  // ownerType='UC', ownerId = stringified solutionMapId (passed through verbatim as the
  // S3 key segment; the backend isolates UC chunks by a distinct uc_id metadata key).
  const kb = useKb('UC', String(detail.solutionMapId));
  // Chat with KB — bind the chat panel to THIS UC (sticky, exclusive with asset
  // scope), then expand it if collapsed. Every subsequent user message carries the
  // UC in context.items so the agent can call retrieve_uc_kb.
  const { setUcScope, chatCollapsed, toggleChat } = useChatPanel();
  const chatWithKb = () => {
    setUcScope({ solutionMapId: detail.solutionMapId, ucName: detail.name });
    if (chatCollapsed) toggleChat();
  };
  // Optimistic just-saved value so the pill doesn't flash the stale value during the
  // parent's reload. Falls back to the authoritative detail.tag.
  const [localTag, setLocalTag] = useState<string | undefined>(undefined);
  const tag = localTag ?? detail.tag;

  const startEdit = () => {
    setDraft(tag);
    setSaveError(null);
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setSaveError(null);
  };
  const save = async () => {
    const next = draft.trim();
    setSaving(true);
    setSaveError(null);
    try {
      // Addressed by the row id, which is also this page's route key. The name-addressed
      // UpsertUcTag wrote `WHERE industry = ? AND use_case = ?` with no status predicate,
      // so it tagged EVERY row with that name including tombstones.
      await getIvtClient().send(
        new UpsertUcTagByIdCommand({ solutionMapId: detail.solutionMapId, tag: next }),
      );
      setLocalTag(next);
      setEditing(false);
      onSaved();
    } catch (e) {
      // 404 = no LIVE UC with this id. A rename no longer causes it (the id survives a
      // rename), so this is a deleted UC — reachable from a tab left open across the
      // delete. The raw English server text is no help; reload and say what to do, same
      // treatment useSolutionMapMutations.updateEntry gives the equivalent stale-tab edit.
      if ((e as { name?: string }).name === 'ResourceNotFoundException') {
        setSaveError('该 UC 已被删除，已重新加载。');
        onSaved();
      } else {
        setSaveError(denialHint(e) ?? ((e as Error).message || '保存失败'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ivt-uc-detail">
      <div className="ivt-uc-detail__title">
        <span className="ivt-uc-detail__name">{detail.name}</span>
        <span className="ivt-uc-detail__industry">{detail.industry}</span>
        {/* Chat with KB: bind the chat panel to THIS UC (sticky scope, mutually
            exclusive with any asset scope). Every subsequent user message carries
            the UC's solutionMapId in context.items so the agent can call
            retrieve_uc_kb. Also expands the panel if collapsed. */}
        <div className="ivt-uc-detail__title-actions">
          <Button iconName="gen-ai" onClick={chatWithKb}>
            Chat with KB
          </Button>
        </div>
      </div>

      {/* SFDC tag — editable, mirrors the Asset detail tag pill (add / edit / save). */}
      {editing ? (
        <div className="ivt-assetd-tag-edit">
          <div className="ivt-assetd-tag-edit__row">
            <div className="ivt-assetd-tag__input">
              <Input
                value={draft}
                onChange={({ detail: d }) => setDraft(d.value)}
                placeholder="输入 SFDC tag…"
                disabled={saving}
                ariaLabel="UC SFDC tag"
                autoFocus
                onKeyDown={({ detail: d }) => {
                  if (d.key === 'Enter') save();
                  if (d.key === 'Escape') cancelEdit();
                }}
              />
            </div>
            <Button variant="inline-icon" iconName="check" ariaLabel="保存 SFDC tag" loading={saving} onClick={save} />
            <Button variant="inline-icon" iconName="close" ariaLabel="取消编辑 SFDC tag" disabled={saving} onClick={cancelEdit} />
          </div>
          {saveError && (
            <div className="ivt-assetd-tag__error">
              <Icon name="status-warning" size="small" variant="error" /> {saveError}
            </div>
          )}
        </div>
      ) : tag ? (
        <div className="ivt-assetd-tag" onClick={startEdit} title="编辑 SFDC tag">
          <span className="ivt-assetd-tag__text">{tag}</span>
          <Button
            variant="inline-icon"
            iconName="edit"
            ariaLabel="编辑 SFDC tag"
            onClick={(e) => {
              e.stopPropagation();
              startEdit();
            }}
          />
        </div>
      ) : (
        <div className="ivt-assetd-tag" onClick={startEdit} title="添加 SFDC tag">
          <span className="ivt-assetd-tag__text ivt-assetd-tag__text--empty">添加 SFDC tag</span>
          <Button
            variant="inline-icon"
            iconName="edit"
            ariaLabel="添加 SFDC tag"
            onClick={(e) => {
              e.stopPropagation();
              startEdit();
            }}
          />
        </div>
      )}

      {/* Knowledge Base — same panel as the Asset detail KB tab, keyed by this UC's
          solutionMapId. Upload / list / download / delete / in-place txt/md edit. */}
      <div className="ivt-uc-detail__kb">
        <KbFilesPanel kb={kb} />
      </div>
    </div>
  );
}
